import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { clearSession, SESSION_CHANGED } from '../auth/session';

// Extend the InternalAxiosRequestConfig to include metadata
declare module 'axios' {
  interface AxiosRequestConfig {
    suppressGlobalError?: boolean;
  }
  interface InternalAxiosRequestConfig {
    metadata?: {
      cacheKey: string;
    };
  }
}

// Simple in-memory cache for API responses
class ApiCache {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

  set(key: string, data: any, ttl: number = 5 * 60 * 1000) { // 5 minutes default TTL
    this.cache.set(key, { data, timestamp: Date.now(), ttl });
  }

  get(key: string): any | null {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > cached.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  delete(key: string) {
    this.cache.delete(key);
  }

  clear() {
    this.cache.clear();
  }

  // Clear cache entries that match a pattern (useful for invalidating related data)
  clearPattern(pattern: string) {
    const regex = new RegExp(pattern);
    // Convert keys to array to avoid iteration issues
    const keys = Array.from(this.cache.keys());
    for (const key of keys) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
  }
}

const apiCache = new ApiCache();

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api',
  timeout: 10000, // 10 second timeout
});

const clearSessionCache = () => {
  apiCache.clear();
  delete api.defaults.headers.common.Authorization;
};
window.addEventListener(SESSION_CHANGED, clearSessionCache);
window.addEventListener('storage', (event) => {
  if (event.key === 'token' || event.key === null) clearSessionCache();
});

// Enhanced request interceptor with caching support
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }

    // Add cache key to config for GET requests
    if (config.method === 'get') {
      const cacheKey = `${token || 'anonymous'}:${config.url}?${new URLSearchParams(config.params).toString()}`;
      config.metadata = { cacheKey };
      
      // Check cache for GET requests
      const cachedData = apiCache.get(cacheKey);
      if (cachedData && !config.headers['Cache-Control']) {
        // Return cached data as a resolved promise
        return Promise.reject({
          isFromCache: true,
          data: cachedData,
          config
        });
      }
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Enhanced response interceptor with caching and better error handling
api.interceptors.response.use(
  response => {
    // Cache successful GET responses
    if (response.config.method === 'get' && response.config.metadata?.cacheKey
        && !response.config.headers?.['Cache-Control'] && !String(response.headers['cache-control'] || '').includes('no-store')) {
      apiCache.set(response.config.metadata.cacheKey, response.data);
    }

    // Clear related cache on mutations
    if (['post', 'put', 'patch', 'delete'].includes(response.config.method?.toLowerCase() || '')) {
      const url = response.config.url || '';
      if (url.includes('/product-transfers')) {
        apiCache.clearPattern('product-transfers');
        apiCache.clearPattern('deposit-products');
      } else if (url.includes('/deposit-products')) {
        apiCache.clearPattern('deposit-products');
        apiCache.clearPattern('products');
      } else if (url.includes('/products')) {
        apiCache.clearPattern('products');
      } else if (url.includes('/categories')) {
        apiCache.clearPattern('categories');
      } else if (url.includes('/companies')) {
        apiCache.clearPattern('companies');
        // Also clear personal-cabinet cache when companies are modified
        apiCache.clearPattern('personal-cabinet');
      } else if (url.includes('/deposits')) {
        apiCache.clearPattern('deposits');
        // Also clear personal-cabinet cache when deposits are modified
        apiCache.clearPattern('personal-cabinet');
      } else if (url.includes('/personal-cabinet')) {
        // Clear all personal-cabinet related cache
        apiCache.clearPattern('personal-cabinet');
        apiCache.clearPattern('companies');
        apiCache.clearPattern('deposits');
      } else if (url.includes('/transactions')) {
        apiCache.clearPattern('transactions');
      }
    }

    return response;
  },
  error => {
    // Handle cached responses
    if (error.isFromCache) {
      return Promise.resolve({
        data: error.data,
        status: 200,
        statusText: 'OK (Cached)',
        headers: {},
        config: error.config
      });
    }

    if (error.response && error.response.status === 401) {
      // Don't redirect on login endpoint failures - let the login page handle it
      const isLoginEndpoint = error.config?.url?.includes('/auth/login');
      if (!isLoginEndpoint) {
        clearSession();
        apiCache.clear(); // Clear cache on logout
        window.location.href = '/login';
      }
    } else if (error.response && error.response.status === 403) {
      if (error.response?.data?.code === 'ACCOUNT_INACTIVE') {
        clearSession();
        sessionStorage.setItem('inactiveAccount', JSON.stringify({}));
        apiCache.clear();
        if (window.location.pathname !== '/account-inactive') window.location.replace('/account-inactive');
        return Promise.reject(error);
      }
      // Don't show alert for login endpoint - let the login page handle it
      const isLoginEndpoint = error.config?.url?.includes('/auth/login');
      if (!isLoginEndpoint) {
        const hasToken = Boolean(localStorage.getItem('token'));
        if (!hasToken) {
          apiCache.clear();
          if (window.location.pathname !== '/login') {
            window.location.replace('/login');
          }
        } else if (!error.config?.suppressGlobalError) {
          alert('Access forbidden. You do not have permission to perform this action.');
        }
      }
    } else if (error.response && error.response.status >= 500 && !error.config?.suppressGlobalError) {
      alert('Server error. Please try again later.');
    } else if (!error.response && !error.config?.suppressGlobalError) {
      alert('Network Error: Please check your connection or try again later.');
    }
    
    return Promise.reject(error);
  }
);

// Enhanced API object with utility methods AND standard HTTP methods
const enhancedApi = {
  // Include all standard axios methods
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.get(url, config),
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.post(url, data, config),
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.put(url, data, config),
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.delete(url, config),
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.patch(url, data, config),
  head: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.head(url, config),
  options: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.options(url, config),
  
  // Include axios instance properties
  defaults: api.defaults,
  interceptors: api.interceptors,
  create: axios.create,
  
  // Force refresh data (bypass cache)
  getWithoutCache: (url: string, config?: AxiosRequestConfig) => {
    return api.get(url, { ...config, headers: { ...config?.headers, 'Cache-Control': 'no-cache' } });
  },

  // Clear specific cache entries
  clearCache: (pattern?: string) => {
    if (pattern) {
      apiCache.clearPattern(pattern);
    } else {
      apiCache.clear();
    }
  },

  // Bulk operations with better error handling
  bulkRequest: async (requests: Array<() => Promise<AxiosResponse>>) => {
    const results = await Promise.allSettled(requests.map(req => req()));
    const successful = results.filter(r => r.status === 'fulfilled').map(r => (r as any).value);
    const failed = results.filter(r => r.status === 'rejected').map(r => (r as any).reason);
    
    if (failed.length > 0) {
      console.warn('Some bulk requests failed:', failed);
    }
    
    return { successful, failed };
  }
};

export default enhancedApi;
